import type { AccessManifest } from "./AccessManifestProvider";

export const CONTEXT_MESH_CAPABILITIES = {
    viewConsole: "context_mesh.console.view",
    viewAnalysis: "context_mesh.analysis.view",
    createMesh: "context_mesh.mesh.create",
    createProduct: "context_mesh.product.create",
    manageFiles: "context_mesh.files.manage",
    manageIntegrations: "context_mesh.integrations.manage"
} as const;

const CONTEXT_MESH_VIEW_CAPABILITY: Readonly<Record<string, string | null>> = {
    "context-mesh-console": CONTEXT_MESH_CAPABILITIES.viewConsole,
    "new-context-mesh": CONTEXT_MESH_CAPABILITIES.createMesh,
    "refresh-context-mesh": CONTEXT_MESH_CAPABILITIES.createMesh,
    "mesh-strength": CONTEXT_MESH_CAPABILITIES.viewAnalysis,
    integrations: CONTEXT_MESH_CAPABILITIES.manageIntegrations,
    uploads: CONTEXT_MESH_CAPABILITIES.manageFiles,
    profile: null
};

export function hasManifestCapability(manifest: AccessManifest | null, capability: string): boolean {
    return manifest?.capabilities.includes(capability) ?? false;
}

/** A standalone mesh detail opens the Hub shell and its analysis surface. */
export function canOpenStandaloneContextMesh(manifest: AccessManifest | null): boolean {
    return Boolean(
        manifest?.entitlements.context_mesh &&
        hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.viewConsole) &&
        hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.viewAnalysis)
    );
}

export function defaultAuthorizedView(manifest: AccessManifest): string {
    if (
        manifest.default_experience === "context_mesh_console" &&
        manifest.entitlements.context_mesh &&
        hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.viewConsole)
    ) {
        return "context-mesh-console";
    }
    if (manifest.default_experience === "product_hub" && manifest.entitlements.product_studio) {
        return "projects";
    }
    if (manifest.default_experience === "account_billing" && manifest.entitlements.account_billing) {
        return "profile";
    }
    if (manifest.entitlements.product_studio) return "projects";
    if (manifest.entitlements.context_mesh && hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.viewConsole)) {
        return "context-mesh-console";
    }
    return "profile";
}

export function canAccessAppView(manifest: AccessManifest | null, view: string): boolean {
    if (!manifest) return false;
    if (manifest.entitlements.product_studio) {
        if (view === "product-context-mesh") return false;
        if (view === "context-mesh-console") {
            return manifest.entitlements.context_mesh &&
                hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.viewConsole);
        }
        if (view === "new-context-mesh") {
            return manifest.entitlements.context_mesh &&
                hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.createMesh);
        }
        return true;
    }
    if (!manifest.entitlements.context_mesh) return view === "profile" && manifest.entitlements.account_billing;

    if (view === "new-product") {
        return hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.createProduct);
    }

    if (!(view in CONTEXT_MESH_VIEW_CAPABILITY)) return false;
    const requiredCapability = CONTEXT_MESH_VIEW_CAPABILITY[view];
    return requiredCapability === null ? manifest.entitlements.account_billing : hasManifestCapability(manifest, requiredCapability);
}

export function resolveAuthorizedView(manifest: AccessManifest, requestedView: string): string {
    return canAccessAppView(manifest, requestedView) ? requestedView : defaultAuthorizedView(manifest);
}
